import type { FC, PropsWithChildren, ReactNode } from 'react';
import React, { Suspense, lazy, useEffect, useState } from 'react';
import type { IEnvironment } from 'relay-runtime';

import { ErrorBoundary } from '../../components/ErrorBoundary';

import { useRelayContext } from './LazyRelayAppWrapper';

// Extract RelayEnvironmentProvider from module exports
// Supports both ESM named exports and CommonJS default exports
type RelayEnvironmentProviderType = FC<{ readonly environment: IEnvironment; readonly children: ReactNode }>;
interface RelayHooksModule {
  readonly RelayEnvironmentProvider?: RelayEnvironmentProviderType;
  readonly default?: {
    readonly RelayEnvironmentProvider?: RelayEnvironmentProviderType;
  };
}
/* istanbul ignore next */
const getRelayEnvironmentProvider = (module: RelayHooksModule): RelayEnvironmentProviderType => {
  const Provider = module.RelayEnvironmentProvider ?? module.default?.RelayEnvironmentProvider;
  if (Provider == null) {
    throw new Error('RelayEnvironmentProvider not found in react-relay/hooks');
  }

  return Provider;
};

const LazyRelayEnvironmentProvider = lazy(() =>
  import(/* webpackChunkName: "4-feat-relay" */ 'react-relay/hooks').then((module) => ({
    default: getRelayEnvironmentProvider(module),
  }))
);

interface LazyRelayModuleWrapperProps {
  /**
   * Rendered while the lazy environment is not ready or the React Relay provider is loading.
   * Defaults to null; suspension within children uses a separate null fallback.
   */
  readonly fallback?: ReactNode;
  /**
   * Rendered when the lazy environment promise rejects during initialization. Defaults to null.
   * Consumers must arrange their own initialization-failure reporting, for example in this UI.
   * This is not the error UI for child rendering or loading the React Relay provider.
   */
  readonly error?: ReactNode;
}
/**
 * Awaits the environment supplied by LazyRelayAppWrapper and loads the React Relay
 * RelayEnvironmentProvider so children can use Relay hooks.
 */
export const LazyRelayModuleWrapper: FC<PropsWithChildren<LazyRelayModuleWrapperProps>> = ({
  children,
  fallback = null,
  error = null,
}) => {
  const relayContext = useRelayContext();
  const [environment, setEnvironment] = useState<IEnvironment | undefined>(undefined);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let aborted = false;

    relayContext.environment
      ?.then((value) => {
        if (aborted) {
          return;
        }

        setEnvironment(value);
      })
      .catch(() => {
        if (aborted) {
          return;
        }

        setHasError(true);
      });

    return () => {
      aborted = true;
    };
  }, [relayContext]);

  if (hasError) {
    return <>{error}</>;
  }

  if (environment == null) {
    return <>{fallback}</>;
  }

  return (
    <Suspense fallback={fallback}>
      <LazyRelayEnvironmentProvider environment={environment}>
        <ErrorBoundary>
          <Suspense fallback={null}>{children}</Suspense>
        </ErrorBoundary>
      </LazyRelayEnvironmentProvider>
    </Suspense>
  );
};
